const createChunker = ({ sampleRate, chunkSeconds, onChunk }) => {
  let buffer = new Int16Array(0);
  let currentChunkSeconds = chunkSeconds;

  const push = (samples) => {
    if (!samples?.length) {
      return;
    }

    const next = new Int16Array(buffer.length + samples.length);
    next.set(buffer, 0);
    next.set(samples, buffer.length);
    buffer = next;

    let chunkSamples = Math.max(1, Math.floor(sampleRate * currentChunkSeconds));
    while (buffer.length >= chunkSamples) {
      const chunk = buffer.slice(0, chunkSamples);
      buffer = buffer.slice(chunkSamples);
      onChunk(chunk);
      chunkSamples = Math.max(1, Math.floor(sampleRate * currentChunkSeconds));
    }
  };

  const flush = () => {
    if (buffer.length) {
      onChunk(buffer.slice(0));
      buffer = new Int16Array(0);
    }
  };

  return {
    push,
    flush,
    updateChunkSeconds: (nextSeconds) => {
      currentChunkSeconds = nextSeconds;
    }
  };
};

export const startAudioCapture = async ({
  targetSampleRate,
  chunkSeconds,
  onChunk,
  onStatus,
  includeMic = false,
  captureSource = "display"
}) => {
  const status = (message) => {
    console.log('[AudioCapture]', message);
    onStatus?.(message);
  };

  console.log('[AudioCapture] Starting with:', { captureSource, includeMic });

  const wantsDisplay = captureSource === "display" || captureSource === "display+mic";
  const wantsMic = captureSource === "mic" || captureSource === "display+mic" || includeMic;

  console.log('[AudioCapture] wantsDisplay:', wantsDisplay, 'wantsMic:', wantsMic);

  let displayStream = null;
  let micStream = null;
  if (wantsMic) {
    status("🎤 Requesting microphone...");
    try {
      micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      console.log('[AudioCapture] Microphone acquired:', micStream.getAudioTracks());
      status("🎤 Microphone acquired!");
    } catch (error) {
      console.error('[AudioCapture] Microphone error:', error);
      if (wantsDisplay) {
        status("⚠️ Microphone unavailable, continuing without mic.");
        micStream = null;
      } else {
        throw new Error("Microphone not available.");
      }
    }
  }

  if (wantsDisplay) {
    status("Requesting system audio (tab/screen)...");
    try {
      displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: "browser" // Prefer tab sharing
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          suppressLocalAudioPlayback: false,
          autoGainControl: true
        },
        preferCurrentTab: false,
        selfBrowserSurface: "include",
        systemAudio: "exclude", // Hint to exclude system audio (Chrome)
        surfaceSwitching: "include",
        monitorTypeSurfaces: "exclude"
      });
    } catch (error) {
      throw new Error(
        "System audio not available. Use 'Share tab audio' or 'Share system audio' in the capture dialog."
      );
    }

    if (!displayStream.getAudioTracks().length) {
      displayStream.getTracks().forEach((track) => track.stop());
      throw new Error(
        "No audio track found. In the share dialog, enable 'Share tab audio' or 'System audio'."
      );
    }
  }

  const audioContext = new AudioContext({
    latencyHint: "interactive"
  });

  await audioContext.resume();

  // Load the PCM processor worklet from public folder
  const workletUrl = process.env.PUBLIC_URL + '/worklets/pcm-processor.js';
  await audioContext.audioWorklet.addModule(workletUrl);

  const workletNode = new AudioWorkletNode(audioContext, "pcm-processor", {
    processorOptions: {
      targetSampleRate
    }
  });

  const chunker = createChunker({
    sampleRate: targetSampleRate,
    chunkSeconds,
    onChunk: (chunk) => onChunk(chunk)
  });

  workletNode.port.onmessage = (event) => {
    const samples = new Int16Array(event.data);
    chunker.push(samples);
  };

  // Mix system + mic into one stream before downsampling in the worklet.
  const mix = audioContext.createGain();
  mix.gain.value = 1;

  const sources = [];

  if (displayStream) {
    const displaySource = audioContext.createMediaStreamSource(displayStream);
    displaySource.connect(mix);
    sources.push(displaySource);
    console.log('[AudioCapture] Display source connected to mixer');
  }

  if (micStream && micStream.getAudioTracks().length) {
    const micSource = audioContext.createMediaStreamSource(micStream);
    micSource.connect(mix);
    sources.push(micSource);
    console.log('[AudioCapture] Microphone source connected to mixer');
  }

  mix.connect(workletNode);
  workletNode.connect(audioContext.destination);

  const hasMic = micStream && micStream.getAudioTracks().length > 0;
  const hasDisplay = displayStream && displayStream.getAudioTracks().length > 0;
  const captureInfo = [];
  if (hasDisplay) captureInfo.push('Tab');
  if (hasMic) captureInfo.push('Mic');

  console.log('[AudioCapture] Final sources:', { hasMic, hasDisplay, sourceCount: sources.length });
  status(`🎙️ Capture active: ${captureInfo.join(' + ') || 'No source'}`);

  const stop = async () => {
    chunker.flush();
    workletNode.port.postMessage({ type: "stop" });
    workletNode.disconnect();
    mix.disconnect();
    sources.forEach((source) => source.disconnect());

    displayStream?.getTracks().forEach((track) => track.stop());
    micStream?.getTracks().forEach((track) => track.stop());

    await audioContext.close();
  };

  return {
    stop,
    updateChunkSeconds: (seconds) => chunker.updateChunkSeconds(seconds),
    sampleRate: targetSampleRate
  };
};
